import { api } from '@packages/backend/convex/_generated/api';
import { getUserErrorMessage } from '@packages/shared';
import { createFileRoute } from '@tanstack/react-router';
import { useConvex } from 'convex/react';
import { useQuery } from 'convex-helpers/react';
import { formatDistanceToNow } from 'date-fns';
import { toast } from 'sonner';
import { ActionButton } from '@/components/ActionButton';
import { CopyTextButton } from '@/components/CopyTextButton';
import { ErrorAlert } from '@/components/ErrorAlert';
import { LoadingIcon } from '@/components/LoadingIcon';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useFeatureFlagEnabled } from '@/features/feature-flags/useFeatureFlagEnabled';

/**
 * The signed-in restaurant user's AI agents: the address to add to one, and the agents they've
 * connected (`packages/backend/convex/mcp/README.md`). Connections belong to the user, not the venue.
 * For venues with the `mcp-server` feature flag on.
 */
export const Route = createFileRoute('/r/$restaurantId/s/ai-agents')({
  component: AiAgentsPage,
});

/**
 * What this page reads of `api.mcp.listConnections`. The component's `McpServer.api()` builds it
 * without a `returns` validator, so static codegen types it `any`.
 */
type Connection = { id: string; name: string; createdAt: number; lastUsedAt?: number };

function AiAgentsPage() {
  const { restaurantId } = Route.useParams();
  const convex = useConvex();
  const isAiAgentsOn = useFeatureFlagEnabled(restaurantId, 'mcp-server');
  const serverUrlQuery = useQuery(api.mcp.getServerUrl, isAiAgentsOn ? {} : 'skip');
  const connectionsQuery = useQuery(api.mcp.listConnections, isAiAgentsOn ? {} : 'skip');

  const handleDisconnect = async (id: string) => {
    try {
      await convex.mutation(api.mcp.revokeConnection, { id });
      toast.success('Agent disconnected');
    } catch (error) {
      toast.error("Couldn't disconnect the agent", { description: getUserErrorMessage(error) });
    }
  };

  if (!isAiAgentsOn) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>AI agents</CardTitle>
          <CardDescription>AI agents aren't turned on for your venue.</CardDescription>
        </CardHeader>
      </Card>
    );
  }
  if (serverUrlQuery.isPending || connectionsQuery.isPending) return <LoadingIcon />;
  if (serverUrlQuery.isError) {
    return <ErrorAlert title="Error loading AI agents" message={serverUrlQuery.error.message} />;
  }
  if (connectionsQuery.isError) {
    return <ErrorAlert title="Error loading AI agents" message={connectionsQuery.error.message} />;
  }

  const connections: Connection[] = connectionsQuery.data;

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>Connect an AI agent</CardTitle>
          <CardDescription>
            Ask Claude, ChatGPT or another AI agent about your menu and online orders, or get it to
            change menu items. Add this address to your agent as a connector, then sign in with your
            OrderHub account.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <span className="inline-flex items-center gap-1 font-mono text-sm">
            {serverUrlQuery.data}
            <CopyTextButton text={serverUrlQuery.data} />
          </span>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Your connected agents</CardTitle>
          <CardDescription>
            Disconnect an agent to stop it using OrderHub straight away.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {connections.length === 0 ? (
            <p className="text-sm text-muted-foreground">No agents connected yet.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Agent</TableHead>
                  <TableHead>Connected</TableHead>
                  <TableHead>Last used</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {connections.map((connection) => (
                  <TableRow key={connection.id}>
                    <TableCell className="font-medium">{connection.name}</TableCell>
                    <TableCell>
                      {formatDistanceToNow(connection.createdAt, { addSuffix: true })}
                    </TableCell>
                    <TableCell>
                      {connection.lastUsedAt
                        ? formatDistanceToNow(connection.lastUsedAt, { addSuffix: true })
                        : 'Never'}
                    </TableCell>
                    <TableCell className="text-right">
                      <ActionButton
                        onClick={() => handleDisconnect(connection.id)}
                        size="sm"
                        variant="outline"
                      >
                        Disconnect
                      </ActionButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </>
  );
}
